import { startTransition } from 'react';
import {
    Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControlLabel, IconButton, InputAdornment, MenuItem, Paper, Popover,
    Slider, Stack, Switch, TextField, Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import BrushIcon from '@mui/icons-material/Brush';
import Colors from '../../../library/Colors';
import { ALL_LANGUAGE_CODES, orderLangs } from '../WebshopLanguagesContext';
import { ConfigCard } from '../style/cards';
import { webshopModalSizing } from '../_webshopUi';
import NavigationModal from '../style/modals/NavigationModal';
import FooterModal from '../style/modals/FooterModal';
import HeaderLogoModal from '../style/modals/HeaderLogoModal';
import ProductDetailInformationModal from '../style/modals/ProductDetailInformationModal';
import GroupsFilterModal from '../style/modals/GroupsFilterModal';
import ProductCardModal from '../style/modals/ProductCardModal';
import ProductGridModal from '../style/modals/ProductGridModal';
import ImageGalleryModal from '../style/modals/ImageGalleryModal';
import ExtraFooterModal from '../style/modals/ExtraFooterModal';
import GlobalWidthSettingsModal from '../style/modals/GlobalWidthSettingsModal';
import { DEFAULT_FONTS, getContrastText } from './configHelpers';
import { ColorField, ConfigSaveButton } from './configWidgets';

// styleTabRenderers — the Opmaak (style) tab preview + section-modal field
// renderers, extracted verbatim from WebshopConfigScreen.js. No hooks live in
// here (verified), so the whole cluster is built per render via a plain
// builder: the screen passes its state/handlers in one ctx object and gets the
// two render functions back. Closure semantics are identical to the inline
// version — the Mock* preview components are (re)created per render exactly as
// before.

// Mock* preview icon components — module scope ON PURPOSE: defining them
// inside the builder gave them a new identity every render, so React
// remounted every icon button on each keystroke while the style tab was
// open. At module scope the identity is stable and React just diffs.
const MockIconBtn = ({ onClick, top, right, left, bottom, children, title, color = 'rgba(0,0,0,0.55)', hoverColor = 'rgba(0,0,0,0.8)' }) => (
    <IconButton
        className="mock-config-icon"
        size="small"
        onClick={onClick}
        title={title}
        sx={{
            position: 'absolute',
            top, right, left, bottom,
            backgroundColor: color,
            color: '#ffffff',
            width: 26,
            height: 26,
            '&:hover': { backgroundColor: hoverColor },
            zIndex: 2,
            '& svg': { fontSize: 16 },
        }}
    >
        {children}
    </IconButton>
);

const MockGear = (props) => (
    <MockIconBtn {...props} title="Settings"><SettingsIcon /></MockIconBtn>
);
const MockBrush = (props) => (
    <MockIconBtn
        {...props}
        title="Colors"
        color="rgba(25,118,210,0.85)"
        hoverColor={Colors.sft_blue}
    >
        <BrushIcon />
    </MockIconBtn>
);

// Stack of icons anchored at a corner — auto-spaces multiple icons
const getAnchorPos = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { top: rect.bottom, left: rect.right };
};

const MockIcons = ({ section, color, config, openStyleModal, top = 4, right = 4 }) => (
    <>
        {/* Visual order (left → right): brush, then gear. 'right' increases = more to the left. */}
        {color && <MockBrush top={top} right={config ? right + 32 : right} onClick={(e) => openStyleModal(section, 'color', getAnchorPos(e))} />}
        {config && <MockGear top={top} right={right} onClick={(e) => openStyleModal(section, 'config', getAnchorPos(e))} />}
    </>
);

export const buildStyleRenderers = (ctx) => {
    const {
        activeStyleSection, setActiveStyleSection, openStyleModal, styleSettings,
        updateStyleSection, updateStyleGlobal, updateStyleVisibility, globalWidths,
        updateGlobalWidths, navigationConfig, updateNavigation, footerConfig,
        updateFooter, extraFooterConfig, updateExtraFooter, headerLogoConfig,
        updateHeaderLogo, uploadHeaderImage, imageGalleryConfig, updateImageGallery,
        groupsFilterConfig, setGroupsFilterConfig, productCardConfig, setProductCardConfig,
        productGridConfig, setProductGridConfig, productDetailInfoConfig,
        updateProductDetailInfo, staticPages, setStaticPage, navbarOrder, setNavbarOrder,
        navFilterStyle, googleFonts, shopFieldLabels, setConfigBlock, setPagesTab,
        setTabValue, saveConfig, configSaving, colorFieldProps, colorPickerInteractionRef,
        isDark, cardBg, borderColor, textColor, mutedText, props,
    } = ctx;

    const SECTION_LABELS = {
        language: 'Language bar',
        header: 'Header (logo)',
        menu: 'Menu bar',
        image: 'Product image',
        title: 'Product title',
        subtitle: 'Product subtitle',
        text: 'Description text',
        price: 'Price',
        addToCart: 'Add to cart button',
        footer: 'Footer',
        footerExtra: 'Footer (extra)',
        sideFilter: 'Groepen sidebar',
        groups: 'Filters and sorting',
        productCard: 'Product information (Verkenner)',
        productDetailInfo: 'Product detail information',
        globalWidths: 'Global width settings',
        grid: 'Product grid',
    };

    // A collapsed sidebar frees up horizontal room → let wide modals grow more.
    const sidebarCollapsed = !!props.hideSidebar;
    // Modal size per section (see webshopModalSizing). Multi-column card sections
    // get the wide canvas; the single-card config sections (Image gallery, Global
    // widths, Language bar, Footer-extra) get the medium canvas so they read full
    // rather than empty. Everything is generous — no more cramped `sm` config modals.
    const styleSec = activeStyleSection?.section;
    const STYLE_WIDE_SECTIONS = ['menu', 'grid', 'footer', 'header', 'productDetailInfo', 'groups', 'productCard'];
    const styleModalTier = STYLE_WIDE_SECTIONS.includes(styleSec) ? 'wide' : 'medium';

    const renderSectionModalFields = () => {
        if (!activeStyleSection) return null;
        const { section, mode } = activeStyleSection;
        const s = styleSettings.sections[section];
        const setField = (field, value) => updateStyleSection(section, field, value);
        // Local i18n helper for the inline modal field labels — same shape
        // as the rest of the admin (`tr(key, fallback)`).
        const tr = (key, fb) => (props.t ? props.t(key, fb) : fb);

        if (mode === 'color') {
            switch (section) {
                case 'language':
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_bg',          'Background')}      value={s.bgColor}    onChange={(v) => setField('bgColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_text',        'Text color')}      value={s.textColor}  onChange={(v) => setField('textColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_active_lang', 'Active language')} value={s.activeColor} onChange={(v) => setField('activeColor', v)} />
                        </Stack>
                    );
                case 'header':
                case 'footer':
                case 'footerExtra':
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_bg',   'Background')} value={s.bgColor}   onChange={(v) => setField('bgColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_text', 'Text color')} value={s.textColor} onChange={(v) => setField('textColor', v)} />
                        </Stack>
                    );
                case 'menu':
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_bg',     'Background')}          value={s.bgColor}     onChange={(v) => setField('bgColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_text',   'Text color')}          value={s.textColor}   onChange={(v) => setField('textColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_accent', 'Accent (cart badge)')} value={s.accentColor} onChange={(v) => setField('accentColor', v)} />
                            <Divider />
                            {/* Custom hover colours — gated by COLOR_USE_CUSTOM_HOVER.
                                Primary = COLOR_PRIMARY_HOVER(_TEXT), Secondary =
                                COLOR_SECONDARY_HOVER(_TEXT). Shown only when enabled. */}
                            <FormControlLabel
                                control={<Switch size="small" checked={!!s.useCustomHover} onChange={(e) => setField('useCustomHover', e.target.checked)} />}
                                label={tr('cfg_style_color_custom_hover', 'Gebruik eigen aangepaste zweefkleuren')}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                            />
                            {s.useCustomHover && (
                                <Stack spacing={2}>
                                    <ColorField {...colorFieldProps} label={tr('cfg_style_color_primary_hover',        'Primary hover')}        value={s.primaryHover}       onChange={(v) => setField('primaryHover', v)} />
                                    <ColorField {...colorFieldProps} label={tr('cfg_style_color_primary_hover_text',   'Primary hover text')}   value={s.primaryHoverText}   onChange={(v) => setField('primaryHoverText', v)} />
                                    <ColorField {...colorFieldProps} label={tr('cfg_style_color_secondary_hover',      'Secondary hover')}      value={s.secondaryHover}     onChange={(v) => setField('secondaryHover', v)} />
                                    <ColorField {...colorFieldProps} label={tr('cfg_style_color_secondary_hover_text', 'Secondary hover text')} value={s.secondaryHoverText} onChange={(v) => setField('secondaryHoverText', v)} />
                                </Stack>
                            )}
                        </Stack>
                    );
                case 'price':
                    // "Main price" colour was removed — that one's now the
                    // global Primary colour. Only the discount/sale tint
                    // stays section-editable.
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_discount', 'Discount / sale')} value={s.discountColor} onChange={(v) => setField('discountColor', v)} />
                        </Stack>
                    );
                case 'sideFilter':
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_border', 'Border color')} value={s.borderColor} onChange={(v) => setField('borderColor', v)} />
                        </Stack>
                    );
                default:
                    return null;
            }
        }

        // mode === 'config'
        switch (section) {
            case 'language': {
                // Two language scopes (stored in styleSettings.visibility):
                //   adminLanguages   = editable in the admin (ADMIN_LANGUAGES)
                //   visibleLanguages = active on the storefront (VISIBILE_LANGUAGES, ⊆ admin)
                const visLangs = styleSettings.visibility.visibleLanguages || [];
                const admLangs = styleSettings.visibility.adminLanguages || [];
                const toggleAdmin = (code) => {
                    if (admLangs.includes(code)) {
                        updateStyleVisibility('adminLanguages', orderLangs(admLangs.filter(c => c !== code)));
                        // No longer editable → can't stay live on the storefront either.
                        if (visLangs.includes(code)) updateStyleVisibility('visibleLanguages', orderLangs(visLangs.filter(c => c !== code)));
                    } else {
                        updateStyleVisibility('adminLanguages', orderLangs([...admLangs, code]));
                    }
                };
                const toggleVisible = (code) => {
                    if (visLangs.includes(code)) {
                        updateStyleVisibility('visibleLanguages', orderLangs(visLangs.filter(c => c !== code)));
                    } else {
                        updateStyleVisibility('visibleLanguages', orderLangs([...visLangs, code]));
                    }
                };
                const langPill = (code, checked, onClick) => (
                    <Box
                        key={code}
                        onClick={() => onClick(code)}
                        sx={{
                            px: 1.5, py: 0.75, borderRadius: 1,
                            border: `1.5px solid ${checked ? Colors.sft_blue : borderColor}`,
                            backgroundColor: checked ? (isDark ? 'rgba(25,118,210,0.12)' : 'rgba(25,118,210,0.08)') : 'transparent',
                            color: checked ? Colors.sft_blue : textColor,
                            fontSize: '0.85rem', fontWeight: checked ? 600 : 400,
                            cursor: 'pointer', userSelect: 'none', transition: 'all 120ms',
                        }}
                    >
                        {code}
                    </Box>
                );
                return (
                    <ConfigCard anchor="cfg-card-language-bar" title={tr('cfg_lang_config_title', 'Taalbalk')}>
                    <Stack spacing={2}>
                        <TextField
                            select
                            autoFocus
                            label={tr('cfg_style_position', 'Position')}
                            size="small"
                            value={s.position}
                            onChange={(e) => setField('position', e.target.value)}
                        >
                            <MenuItem value="left">{tr('cfg_style_position_left',     'Left')}</MenuItem>
                            <MenuItem value="center">{tr('cfg_style_position_center', 'Center')}</MenuItem>
                            <MenuItem value="right">{tr('cfg_style_position_right',   'Right')}</MenuItem>
                        </TextField>
                        <Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1 }}>
                                {tr('cfg_style_max_width', 'Max-width')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Slider
                                    value={s.widthPx}
                                    min={0}
                                    max={2500}
                                    step={10}
                                    onChange={(_e, v) => setField('widthPx', v)}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(v) => `${v}px`}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    type="number"
                                    size="small"
                                    value={s.widthPx}
                                    onChange={(e) => setField('widthPx', Math.max(0, Math.min(2500, parseInt(e.target.value) || 0)))}
                                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                    sx={{ width: 110 }}
                                />
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1 }}>
                                {tr('cfg_lang_admin_available', 'Beschikbaar in de admin')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {ALL_LANGUAGE_CODES.map(code => langPill(code, admLangs.includes(code), toggleAdmin))}
                            </Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.75 }}>
                                {tr('cfg_lang_admin_available_hint', 'Talen waarvoor je teksten kan invullen in de admin.')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1 }}>
                                {tr('cfg_lang_storefront_active', 'Actief op de webshop')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 32, alignItems: 'center' }}>
                                {admLangs.length
                                    ? orderLangs(admLangs).map(code => langPill(code, visLangs.includes(code), toggleVisible))
                                    : <Typography variant="caption" sx={{ color: mutedText }}>{tr('cfg_lang_none_available', 'Maak eerst een taal beschikbaar.')}</Typography>}
                            </Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.75 }}>
                                {tr('cfg_lang_storefront_active_hint', 'Welke van die talen klanten effectief zien op de webshop.')}
                            </Typography>
                        </Box>
                    </Stack>
                    </ConfigCard>
                );
            }
            case 'header':
                // Header cog opens the Header / Logo modal — Size + Images cards.
                return (
                    <HeaderLogoModal
                        value={headerLogoConfig}
                        onChange={updateHeaderLogo}
                        onUploadImage={uploadHeaderImage}
                        globalHeaderWidth={globalWidths.perElement?.header}
                        onChangeGlobalHeaderWidth={(patch) => {
                            if (patch.width !== undefined)       updateGlobalWidths('perElement.header.width',       patch.width);
                            if (patch.bgFullWidth !== undefined) updateGlobalWidths('perElement.header.bgFullWidth', patch.bgFullWidth);
                        }}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'productDetailInfo':
                // The big one — eight cards covering layout, sections,
                // details list, language texts, title/url, breadcrumb,
                // related products, stock visibility.
                return (
                    <ProductDetailInformationModal
                        value={productDetailInfoConfig}
                        onChange={updateProductDetailInfo}
                        shopFieldLabels={shopFieldLabels}
                        detailPage={staticPages.detail}
                        onDetailPageChange={setStaticPage('detail')}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'groups':
                // Filters & sorting — opened from the cog on the GROEPEN
                // sidebar in the Verkenner mockup.
                return (
                    <GroupsFilterModal
                        value={groupsFilterConfig}
                        onChange={setGroupsFilterConfig}
                        shopFieldLabels={shopFieldLabels}
                        filterStyle={navFilterStyle}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'productCard':
                // Product card layout — opened from the cog on the IMG
                // block of the last product card in the Verkenner mockup.
                // Controlled by hoisted state so the Verkenner preview
                // reflects edits live.
                return (
                    <ProductCardModal
                        value={productCardConfig}
                        onChange={setProductCardConfig}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'grid':
                // Product grid + variant grouping — opened from the small
                // cog that floats between cards 2 and 3 in the Verkenner
                // mockup (i.e. centred above the 4 mock cards).
                return (
                    <ProductGridModal
                        value={productGridConfig}
                        onChange={setProductGridConfig}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'globalWidths':
                // Centralised max-width settings — opened from the dedicated
                // sidebar button (too tall to embed inline in the sidebar).
                return (
                    <GlobalWidthSettingsModal
                        value={globalWidths}
                        onChange={updateGlobalWidths}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'image':
                // Image cog now opens the full Image Gallery modal —
                // thumbnail position picker, grid/scroll mode, max thumbs,
                // arrows + per-element colours.
                return (
                    <ImageGalleryModal
                        value={imageGalleryConfig}
                        onChange={updateImageGallery}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            // NOTE: the 'text' / 'title' / 'subtitle' sections have no cog
            // opener anymore — their styling moved into the Product-card modal
            // (section='productCard'). The old inline font-size/position cases
            // were unreachable dead code and were removed.
            case 'menu':
                // The menu cog now opens the full Navigation modal — composed
                // of Header content / Layout / Navigation menu / Behaviour
                // cards (each in its own file under style/cards/).
                return (
                    <NavigationModal
                        value={navigationConfig}
                        onChange={updateNavigation}
                        shopFieldLabels={shopFieldLabels}
                        navbarOrder={navbarOrder}
                        onNavbarOrderChange={setNavbarOrder}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'footer':
                // Footer cog opens the full Footer modal (Width / Pages /
                // Custom links / Social media / Contact / Newsletter cards).
                // Colour-only settings stay in the brush popover above.
                return (
                    <FooterModal
                        value={footerConfig}
                        onChange={updateFooter}
                        globalFooterWidth={globalWidths.perElement?.footer}
                        onChangeGlobalFooterWidth={(patch) => {
                            if (patch.width !== undefined)        updateGlobalWidths('perElement.footer.width',       patch.width);
                            if (patch.bgFullWidth !== undefined)  updateGlobalWidths('perElement.footer.bgFullWidth', patch.bgFullWidth);
                        }}
                        onEditPage={(slug) => {
                            // The "Edit page" buttons in PagesAndLinksCard
                            // are navigation shortcuts to the dedicated
                            // page editors in the Pages tab. We close the
                            // FooterModal dialog and jump to the matching
                            // sub-tab. Slugs without an internal page
                            // ('terms') have their button disabled in the
                            // card so this handler never sees them.
                            const PAGE_SUB_INDEX = { about: 4, disclaimer: 5, privacy: 6 };
                            const idx = PAGE_SUB_INDEX[slug];
                            if (idx === undefined) return;
                            setConfigBlock(null);
                            startTransition(() => {
                                setTabValue(0);     // Pages tab
                                setPagesTab(idx);
                            });
                        }}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'footerExtra':
                // Extra-footer cog opens the full Extra Footer modal:
                // 3 columns × 3 languages, with column 1 unlayer and
                // columns 2-3 wysiwyg, plus a payment-methods toggle.
                return (
                    <ExtraFooterModal
                        value={extraFooterConfig}
                        onChange={updateExtraFooter}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            default:
                return null;
        }
    };

    const renderStyleTab = (pageMode = 'productDetail') => {
        const g = styleSettings.global;
        const vis = styleSettings.visibility;
        const sec = styleSettings.sections;
        const tr  = (key, fb) => (props.t ? props.t(key, fb) : fb);

        return (
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
                gap: 3,
                alignItems: 'start',
            }}>

                {/* ===== LEFT COLUMN: Global + Visibility ===== */}
                {/* Compact panels — caption-sized titles, tighter spacing,
                    compact ColorFields, smaller switches. The whole sidebar
                    fits in roughly half the vertical space the v1 design
                    used. */}
                <Stack spacing={1.5}>
                    {/* Global */}
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.6, color: mutedText, display: 'block', mb: 1, lineHeight: 1.4 }}>
                            {tr('cfg_style_global_title', 'Global')}
                        </Typography>
                        <Stack spacing={1}>
                            <ColorField {...colorFieldProps} compact label={tr('cfg_style_global_bg',      'Background color')} value={g.background} onChange={(v) => updateStyleGlobal('background', v)} />
                            <ColorField {...colorFieldProps} compact label={tr('cfg_style_global_text',    'Text color')}       value={g.text}       onChange={(v) => updateStyleGlobal('text', v)} />
                            <ColorField {...colorFieldProps} compact label={tr('cfg_style_global_primary', 'Primary color')}    value={g.primary}    onChange={(v) => updateStyleGlobal('primary', v)} />
                            <Autocomplete
                                size="small"
                                fullWidth
                                disableClearable
                                options={googleFonts.length > 0 ? googleFonts : [
                                    ...DEFAULT_FONTS,
                                    { family: 'Roboto',      category: 'sans-serif' },
                                    { family: 'Inter',       category: 'sans-serif' },
                                    { family: 'Open Sans',   category: 'sans-serif' },
                                    { family: 'Lato',        category: 'sans-serif' },
                                    { family: 'Nunito',      category: 'sans-serif' },
                                    { family: 'Montserrat',  category: 'sans-serif' },
                                    { family: 'Poppins',     category: 'sans-serif' },
                                ]}
                                value={googleFonts.find(f => f.family === g.font) || { family: g.font, category: 'sans-serif' }}
                                onChange={(_, newValue) => {
                                    if (newValue?.family) updateStyleGlobal('font', newValue.family);
                                }}
                                isOptionEqualToValue={(opt, val) => opt.family === val.family}
                                getOptionLabel={(opt) => opt?.family || ''}
                                groupBy={(opt) => opt.category}
                                renderOption={(props, opt) => (
                                    <li {...props} key={opt.family} style={{ fontFamily: `"${opt.family}"`, fontSize: '0.85rem' }}>
                                        {opt.family}
                                    </li>
                                )}
                                renderInput={(params) => <TextField {...params} label={tr('cfg_style_global_font', 'Font')} sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }} />}
                            />
                            {/* Button border-radius (BUTTON_BORDER_RADIUS) — a
                                new-admin-only key, created on first save via the
                                mini-api creatable whitelist. State rides on
                                globalWidths so load/save/dirty stay in one place. */}
                            <TextField
                                type="number"
                                size="small"
                                fullWidth
                                label={tr('cfg_style_global_button_radius', 'Button rounding')}
                                title={tr('cfg_style_global_button_radius_help', 'Border-radius applied to buttons across the webshop (e.g. add to cart). 0 = square corners.')}
                                value={globalWidths.buttonBorderRadius ?? 4}
                                onChange={(e) => updateGlobalWidths('buttonBorderRadius', Math.max(0, parseInt(e.target.value) || 0))}
                                InputProps={{ endAdornment: <InputAdornment position="end"><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>px</Typography></InputAdornment> }}
                                inputProps={{ min: 0, step: 1 }}
                                sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
                            />
                        </Stack>
                    </Paper>

                    {/* Visibility */}
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.6, color: mutedText, display: 'block', mb: 0.5, lineHeight: 1.4 }}>
                            {tr('cfg_style_visibility_title', 'Visibility')}
                        </Typography>
                        <Stack spacing={0}>
                            <FormControlLabel
                                control={<Switch color="success" size="small" checked={vis.languageBar} onChange={(e) => updateStyleVisibility('languageBar', e.target.checked)} />}
                                label={<Typography variant="caption" sx={{ fontSize: '0.78rem' }}>{tr('cfg_style_visibility_language_bar', 'Language bar')}</Typography>}
                                labelPlacement="start"
                                sx={{ ml: 0, mr: 0, justifyContent: 'space-between', width: '100%' }}
                            />
                            <FormControlLabel
                                control={<Switch color="success" size="small" checked={vis.footer} onChange={(e) => updateStyleVisibility('footer', e.target.checked)} />}
                                label={<Typography variant="caption" sx={{ fontSize: '0.78rem' }}>{tr('cfg_style_visibility_footer', 'Footer')}</Typography>}
                                labelPlacement="start"
                                sx={{ ml: 0, mr: 0, justifyContent: 'space-between', width: '100%' }}
                            />
                            <FormControlLabel
                                control={<Switch color="success" size="small" checked={vis.extra} onChange={(e) => updateStyleVisibility('extra', e.target.checked)} />}
                                label={<Typography variant="caption" sx={{ fontSize: '0.78rem' }}>{tr('cfg_style_visibility_extra_footer', 'Extra footer')}</Typography>}
                                labelPlacement="start"
                                sx={{ ml: 0, mr: 0, justifyContent: 'space-between', width: '100%' }}
                            />
                            <Box sx={{ mt: 0.75 }}>
                                <TextField
                                    select
                                    label={tr('cfg_style_visibility_back_to_top', 'Back-to-top position')}
                                    size="small"
                                    value={vis.backToTopPosition}
                                    onChange={(e) => updateStyleVisibility('backToTopPosition', e.target.value)}
                                    fullWidth
                                    sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
                                >
                                    <MenuItem value="left">{tr('cfg_style_visibility_btt_left',   'Bottom left')}</MenuItem>
                                    <MenuItem value="center">{tr('cfg_style_visibility_btt_center', 'Bottom center')}</MenuItem>
                                    <MenuItem value="right">{tr('cfg_style_visibility_btt_right',  'Bottom right')}</MenuItem>
                                    <MenuItem value="hidden">{tr('cfg_style_visibility_btt_hidden', 'Hidden')}</MenuItem>
                                </TextField>
                            </Box>
                        </Stack>
                    </Paper>

                    {/* Global width settings — opens its own modal because
                        the full per-element list is too tall for the sidebar.
                        Same data is also reachable from the per-section
                        modals (Header / Footer / banners). */}
                    <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
                            onClick={() => openStyleModal('globalWidths', 'config')}
                            fullWidth
                            sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.5 }}
                        >
                            {props.t ? props.t('cfg_widths_open', 'Global width settings') : 'Global width settings'}
                        </Button>
                    </Paper>

                </Stack>

                {/* ===== RIGHT COLUMN: Webshop mockup ===== */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: `1px solid ${borderColor}`,
                        backgroundColor: g.background,
                        color: g.text,
                        fontFamily: g.font,
                        overflow: 'hidden',
                        position: 'relative',
                        // Force every descendant to inherit the mockup font.
                        // MUI Typography variants (h1/body1/etc.) have more specific
                        // selectors than "& *", so we need !important to win the
                        // cascade. Scoped to this Paper only.
                        '& *': { fontFamily: 'inherit !important' },
                        // Hide the gear/brush config icons until the user hovers
                        // the mockup — lets you see a clean preview.
                        '& .mock-config-icon': {
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'opacity 0.15s ease',
                        },
                        '&:hover .mock-config-icon': {
                            opacity: 1,
                            pointerEvents: 'auto',
                        },
                    }}
                >
                    {/* Language bar */}
                    {vis.languageBar && (
                        <Box sx={{
                            position: 'relative',
                            backgroundColor: sec.language.bgColor,
                            color: sec.language.textColor,
                            px: 2, py: 0.75,
                            fontSize: '0.8rem',
                            borderBottom: `1px solid ${borderColor}`,
                            pr: 10, // reserve space for icons so they never overlap
                        }}>
                            <Box sx={{
                                display: 'flex',
                                gap: 1.5,
                                // 0 = no constraint (natural content width); otherwise cap at the chosen max.
                                ...(sec.language.widthPx > 0 ? { maxWidth: `${sec.language.widthPx}px`, width: '100%' } : {}),
                                marginLeft: sec.language.position === 'left' ? 0 : 'auto',
                                marginRight: sec.language.position === 'right' ? 0 : 'auto',
                                // Inner alignment is opposite of outer position, so increasing
                                // width pushes the whole (still-packed) language group inward.
                                justifyContent: sec.language.position === 'right' ? 'flex-start'
                                    : sec.language.position === 'left' ? 'flex-end'
                                        : 'center',
                            }}>
                                {(styleSettings.visibility.visibleLanguages || []).map((lang, idx) => (
                                    <span
                                        key={lang}
                                        // COLOR_TEXT_ACTIVE_LANG is a TEXT colour (default #fff). Apply it as
                                        // `color` rather than `backgroundColor` so the active language is just
                                        // visually emphasised, not painted as a solid block.
                                        style={idx === 0 ? { color: sec.language.activeColor, fontWeight: 600 } : undefined}
                                    >
                                        {lang}
                                    </span>
                                ))}
                            </Box>
                            <MockIcons openStyleModal={openStyleModal} section="language" color config top={4} right={6} />
                        </Box>
                    )}

                    {/* Header */}
                    <Box sx={{
                        position: 'relative',
                        backgroundColor: sec.header.bgColor,
                        color: sec.header.textColor,
                        py: 3,
                        textAlign: 'center',
                        letterSpacing: 1,
                    }}>
                        <Typography sx={{ color: sec.header.textColor, fontWeight: 600, fontSize: '1.1rem' }}>
                            [LOGO]
                        </Typography>
                        <MockIcons openStyleModal={openStyleModal} section="header" color config top={8} right={8} />
                    </Box>

                    {/* Menu */}
                    <Box sx={{
                        position: 'relative',
                        backgroundColor: sec.menu.bgColor,
                        color: sec.menu.textColor,
                        py: 1.25,
                        px: 2,
                        display: 'flex',
                        justifyContent: sec.menu.alignment === 'left' ? 'flex-start'
                            : sec.menu.alignment === 'right' ? 'flex-end'
                                : 'center',
                        gap: 3,
                        fontSize: '0.85rem',
                        pr: 10,
                    }}>
                        <span>Category</span>
                        <span>|</span>
                        <span>Category</span>
                        <span>|</span>
                        <span>Category</span>
                        <span>|</span>
                        <span style={{ position: 'relative' }}>
                            Cart
                            {sec.menu.showCartBadge && (
                                <span style={{
                                    position: 'absolute',
                                    top: -6, right: -10,
                                    backgroundColor: sec.menu.accentColor,
                                    color: '#fff',
                                    fontSize: '0.65rem',
                                    width: 14, height: 14,
                                    borderRadius: '50%',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>3</span>
                            )}
                        </span>
                        <span>|</span>
                        <span>Account</span>
                        <MockIcons openStyleModal={openStyleModal} section="menu" color config top={6} right={8} />
                    </Box>

                    {/* ── Page-specific body ─────────────────────────────────
                        Shared sections above (language/header/menu) and below
                        (footer/footerExtra) render the same for both sub-tabs.
                        Editing them on one tab automatically reflects on the
                        other because they read from the same styleSettings
                        state. The body below is the only thing that swaps
                        between Explorer and Product detail. */}
                    {pageMode === 'explorer' ? (
                    <>
                        {/* Sidebar + grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 2, p: 2 }}>
                            {/* Sidebar — only the HEADER strip uses the menu's
                                bg + text colours; the body stays transparent so
                                the page background shows through. The outer
                                border is editable (maps to COLOR_BORDERS). */}
                            <Paper sx={{
                                position: 'relative',
                                borderRadius: 1,
                                backgroundColor: 'transparent',
                                border: `1px solid ${sec.sideFilter.borderColor}`,
                                boxShadow: 'none',
                                overflow: 'hidden',
                            }}>
                                {/* Two separate sections in the same corner —
                                    brush opens the sideFilter color modal, cog
                                    opens the groups filters & sorting modal.
                                    Cog stays rightmost; brush sits to its left. */}
                                <MockIcons openStyleModal={openStyleModal} section="sideFilter" color  top={4} right={36} />
                                <MockIcons openStyleModal={openStyleModal} section="groups"     config top={4} right={4}  />
                                {/* Coloured header strip */}
                                <Box sx={{
                                    backgroundColor: sec.menu.bgColor,
                                    color: sec.menu.textColor,
                                    px: 1.5, py: 1,
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                }}>
                                    GROEPEN
                                </Box>
                                {/* Body — uses the global text colour so the
                                    category list reads against the page bg, not
                                    the menu's textColor. */}
                                <Stack spacing={0.5} sx={{ fontSize: '0.8rem', color: g.text, p: 1.5 }}>
                                    <span style={{ fontWeight: 600 }}>KLEDING</span>
                                    {['Bh', 'Blazer', 'Blouse', 'Body', 'Cardigan', 'Chino', 'Dress', 'Hoody', 'Jacket & coat'].map((c) => (
                                        <span key={c} style={{ paddingLeft: 8 }}>{c}</span>
                                    ))}
                                    <span style={{ fontWeight: 600, marginTop: 6 }}>SCHOENEN</span>
                                    <span style={{ fontWeight: 600 }}>ACCESSOIRES</span>
                                </Stack>
                            </Paper>

                            {/* Right column: sort bar + grid */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {/* Sort/filter toolbar */}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    {['MERKEN', 'MATEN', 'PRIJS', 'CATEGORIE'].map((label) => (
                                        <Box key={label} sx={{
                                            px: 1.25, py: 0.5,
                                            border: `1px solid ${borderColor}`,
                                            borderRadius: 0.75,
                                            fontSize: '0.7rem',
                                            color: sec.title.textColor,
                                            backgroundColor: '#ffffff',
                                        }}>
                                            {label} ▾
                                        </Box>
                                    ))}
                                    <Box sx={{ flex: 1 }} />
                                    <Box sx={{
                                        px: 1.25, py: 0.5,
                                        border: `1px solid ${borderColor}`,
                                        borderRadius: 0.75,
                                        fontSize: '0.7rem',
                                        color: sec.title.textColor,
                                        backgroundColor: '#ffffff',
                                    }}>
                                        Sorteren ▾
                                    </Box>
                                </Box>

                                {/* Product grid (4 mockup cards) — wrapped in a
                                    relatively-positioned Box so the grid-config
                                    cog can float at the top center, sitting
                                    visually between cards 2 and 3.

                                    FUTURE: when the storefront supports an
                                    outlined product-card wrapper, flip
                                    productGridConfig.productGrid.showCardOutline
                                    on (and expose the toggle in the modal) to
                                    light up the bordered look below. Today the
                                    flag stays false so the preview matches what
                                    the live shop renders. */}
                                <Box sx={{ position: 'relative' }}>
                                    {/* Grid cog: sits at the seam between card 2
                                        and card 3. Slight negative top moves it
                                        above the cards so it doesn't crowd the
                                        IMG content. */}
                                    <IconButton
                                        className="mock-config-icon"
                                        size="small"
                                        onClick={(e) => openStyleModal('grid', 'config', getAnchorPos(e))}
                                        title="Grid settings"
                                        sx={{
                                            position: 'absolute',
                                            top: -14,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: 26, height: 26,
                                            backgroundColor: 'rgba(0,0,0,0.55)',
                                            color: '#fff',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                                            '& svg': { fontSize: 15 },
                                            zIndex: 3,
                                        }}
                                    >
                                        <SettingsIcon />
                                    </IconButton>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 1fr)',
                                    gap: 1.5,
                                }}>
                                    {[1, 2, 3, 4].map((i) => {
                                        const showOutline = !!productGridConfig?.productGrid?.showCardOutline;
                                        return (
                                        <Paper key={i} variant={showOutline ? 'outlined' : 'elevation'} elevation={0} sx={{
                                            p: 1.25,
                                            borderRadius: showOutline ? 1 : 0,
                                            backgroundColor: showOutline ? '#ffffff' : 'transparent',
                                            border: showOutline ? undefined : 'none',
                                            position: 'relative',
                                            overflow: 'hidden',
                                        }}>
                                            {i === 4 && (
                                                <>
                                                    {/* Discount badge sits one position inward so the
                                                        brush can take the rightmost corner — same visual
                                                        rhythm as the productDetail subtab where the brush
                                                        sits to the right of the discount. */}
                                                    <Box sx={{
                                                        position: 'absolute', top: 6, right: 36,
                                                        backgroundColor: sec.price.discountColor,
                                                        color: '#fff',
                                                        px: 0.5, py: 0.1,
                                                        borderRadius: 0.5,
                                                        fontSize: '0.6rem',
                                                        fontWeight: 700,
                                                        zIndex: 2,
                                                    }}>-30%</Box>
                                                    {/* Brush at top-right corner, just to the right of
                                                        the discount badge — opens the price section's
                                                        color modal. */}
                                                    <IconButton
                                                        className="mock-config-icon"
                                                        size="small"
                                                        onClick={(e) => openStyleModal('price', 'color', getAnchorPos(e))}
                                                        title="Colors"
                                                        sx={{
                                                            position: 'absolute', top: 4, right: 6,
                                                            width: 22, height: 22,
                                                            backgroundColor: 'rgba(25,118,210,0.85)',
                                                            color: '#fff',
                                                            '&:hover': { backgroundColor: Colors.sft_blue },
                                                            '& svg': { fontSize: 13 },
                                                            zIndex: 2,
                                                        }}
                                                    >
                                                        <BrushIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                            <Box sx={{
                                                position: 'relative',
                                                width: '100%', aspectRatio: '3 / 4',
                                                backgroundColor: '#f3f3f3',
                                                borderRadius: 0.5,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#999', fontSize: '0.7rem',
                                                mb: 0.75,
                                            }}>
                                                IMG
                                                {i === 4 && (
                                                    /* Cog overlays at center, offset right of "IMG" text —
                                                        absolute positioning keeps IMG truly centered. */
                                                    <IconButton
                                                        className="mock-config-icon"
                                                        size="small"
                                                        onClick={(e) => openStyleModal('productCard', 'config', getAnchorPos(e))}
                                                        title="Settings"
                                                        sx={{
                                                            position: 'absolute',
                                                            left: 'calc(50% + 18px)',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            width: 22, height: 22,
                                                            backgroundColor: 'rgba(0,0,0,0.55)',
                                                            color: '#fff',
                                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                                                            '& svg': { fontSize: 13 },
                                                        }}
                                                    >
                                                        <SettingsIcon />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            {/* Product info — title, optional divider, subtitle, price.
                                                Layout pulled from productCardConfig so live edits in the
                                                Product information modal are reflected here. */}
                                            <Typography sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: sec.title.textColor,
                                                textAlign: productCardConfig.productInfo.align || 'left',
                                                pb: productCardConfig.productInfo.showDivider ? 0.4 : 0,
                                                borderBottom: productCardConfig.productInfo.showDivider ? '1px solid lightgrey' : 'none',
                                                mb: productCardConfig.productInfo.showDivider ? 0.4 : 0,
                                            }}>
                                                Product {i}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '0.65rem',
                                                color: sec.subtitle.textColor,
                                                textAlign: productCardConfig.productInfo.align || 'left',
                                            }}>
                                                ARTICLE.{i}0010101
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '0.8rem',
                                                color: g.primary,
                                                fontWeight: 600,
                                                mt: 0.25,
                                                textAlign: productCardConfig.productInfo.align || 'left',
                                            }}>
                                                € {(99 + i * 10).toFixed(2)}
                                            </Typography>
                                        </Paper>
                                        );
                                    })}
                                </Box>
                                </Box>
                            </Box>
                        </Box>
                    </>
                    ) : (
                    /* Product row */
                    <>
                    {/* The page-level cog used to live in a spacer band above
                        the breadcrumb. It now sits inline with the product title
                        (rendered inside the product row below) so it aligns
                        with the focal point of the page. */}
                    {/* Breadcrumb — driven by the Product detail info modal's
                        breadcrumb settings. Shown above the product so the
                        preview honestly reflects how much vertical space the
                        crumb-row eats. */}
                    {productDetailInfoConfig.breadcrumb?.enabled !== false && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 2, fontSize: '0.78rem', color: g.text }}>
                            <span style={{ opacity: 0.7 }}>◀ Vorige</span>
                            <span style={{ opacity: 0.3 }}>|</span>
                            {productDetailInfoConfig.breadcrumb?.showRootLink && (
                                <>
                                    {/* Crumb links use the regular breadcrumb text
                                        colour (inherited from the row's `color: g.text`),
                                        not the primary web colour — matches the live
                                        storefront where the whole trail is one colour
                                        and only the current page is emphasised. */}
                                    <span>Shop</span>
                                    <span style={{ opacity: 0.5 }}>{productDetailInfoConfig.breadcrumb?.separator || '/'}</span>
                                </>
                            )}
                            <span>KLEDING</span>
                            <span style={{ opacity: 0.5 }}>{productDetailInfoConfig.breadcrumb?.separator || '/'}</span>
                            <span style={{ fontWeight: 600 }}>Blouse</span>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, p: 3, borderBottom: `1px solid ${borderColor}`, position: 'relative' }}>
                        {/* Page-level cog (Product detail information modal).
                            Aligned with the language/header/menu cogs above
                            (right: 8) so the right column of cogs reads as a
                            consistent vertical line. */}
                        <MockIcons openStyleModal={openStyleModal} section="productDetailInfo" config top={8} right={8} />
                        {/* Image gallery — main image + thumbnail strip,
                            laid out per the Image gallery modal's
                            thumbnailPosition setting. */}
                        <Box sx={{
                            position: 'relative',
                            width: `${sec.image.width}%`,
                            minWidth: 160,
                        }}>
                            {(() => {
                                const pos        = imageGalleryConfig.thumbnailPosition || 'left';
                                const maxThumbs  = Math.max(1, Math.min(8, Number(imageGalleryConfig.maxThumbnails) || 3));
                                const showArrows = imageGalleryConfig.showArrows;
                                const arrowFg    = imageGalleryConfig.arrowColor   || '#000';
                                const arrowBg    = imageGalleryConfig.arrowBgColor || '#fff';

                                const main = (
                                    <Box sx={{
                                        position: 'relative',
                                        flex: 1,
                                        aspectRatio: '1 / 1',
                                        backgroundColor: '#f3f3f3',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#999',
                                        fontSize: '0.8rem',
                                    }}>
                                        IMG
                                        {showArrows && (
                                            <>
                                                <Box sx={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, borderRadius: '50%', backgroundColor: arrowBg, color: arrowFg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>‹</Box>
                                                <Box sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, borderRadius: '50%', backgroundColor: arrowBg, color: arrowFg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>›</Box>
                                            </>
                                        )}
                                    </Box>
                                );
                                const thumbs = (orientation) => (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: orientation === 'col' ? 'column' : 'row',
                                        gap: 0.75,
                                        flexShrink: 0,
                                        ...(orientation === 'col'
                                            ? { width: 56 }
                                            : { width: '100%' }),
                                    }}>
                                        {Array.from({ length: maxThumbs }).map((_, i) => (
                                            <Box key={i} sx={{
                                                ...(orientation === 'col' ? { width: '100%' } : { flex: 1 }),
                                                aspectRatio: '1 / 1',
                                                backgroundColor: '#ececec',
                                                borderRadius: 0.5,
                                                border: i === 0 ? `1px solid ${g.primary}` : '1px solid transparent',
                                            }} />
                                        ))}
                                    </Box>
                                );

                                if (pos === 'hidden') return main;
                                if (pos === 'above')  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{thumbs('row')}{main}</Box>;
                                if (pos === 'below')  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{main}{thumbs('row')}</Box>;
                                if (pos === 'right')  return <Box sx={{ display: 'flex', gap: 1 }}>{main}{thumbs('col')}</Box>;
                                /* default 'left' */    return <Box sx={{ display: 'flex', gap: 1 }}>{thumbs('col')}{main}</Box>;
                            })()}
                            <MockIcons openStyleModal={openStyleModal} section="image" config top={6} right={6} />
                        </Box>

                        {/* Info */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {/* Title + subtitle — tight pair, subtitle sits
                                directly under the title. */}
                            <Box sx={{ position: 'relative', textAlign: sec.title.position }}>
                                <Typography sx={{
                                    color: sec.title.textColor,
                                    fontSize: sec.title.fontSize,
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                }}>
                                    Product title
                                </Typography>
                                <Typography sx={{
                                    color: sec.subtitle.textColor,
                                    fontSize: sec.subtitle.fontSize,
                                    fontStyle: 'italic',
                                    mt: 0.25,
                                    lineHeight: 1.2,
                                }}>
                                    Product subtitle
                                </Typography>
                            </Box>

                            {/* Price — own line, larger, with € sign */}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25 }}>
                                <Typography sx={{
                                    color: g.primary,
                                    fontWeight: 700,
                                    fontSize: '1.5rem',
                                    lineHeight: 1,
                                }}>
                                    € 90,00
                                </Typography>
                                <Typography sx={{
                                    textDecoration: 'line-through',
                                    color: '#aaa',
                                    fontSize: '0.95rem',
                                }}>
                                    € 100,00
                                </Typography>
                                <Box sx={{
                                    backgroundColor: sec.price.discountColor,
                                    color: '#fff',
                                    px: 0.75, py: 0.25,
                                    borderRadius: 0.5,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    alignSelf: 'center',
                                }}>-10%</Box>
                                {/* Brush sits inline next to the discount badge,
                                    on the same vertical line — opens the price
                                    section's color modal. */}
                                <IconButton
                                    className="mock-config-icon"
                                    size="small"
                                    onClick={(e) => openStyleModal('price', 'color', getAnchorPos(e))}
                                    title="Colors"
                                    sx={{
                                        alignSelf: 'center',
                                        width: 22, height: 22,
                                        backgroundColor: 'rgba(25,118,210,0.85)',
                                        color: '#fff',
                                        '&:hover': { backgroundColor: Colors.sft_blue },
                                        '& svg': { fontSize: 13 },
                                    }}
                                >
                                    <BrushIcon />
                                </IconButton>
                            </Box>

                            {/* Description */}
                            <Box sx={{
                                backgroundColor: '#f3f3f3',
                                borderRadius: 1,
                                p: 1.25,
                                minHeight: 60,
                            }}>
                                <Typography sx={{ color: sec.text.textColor, fontSize: sec.text.fontSize }}>
                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                                </Typography>
                            </Box>

                            {/* Add to cart — colour is driven by the global Primary
                                colour, no per-section editor (text colour is
                                computed for contrast). */}
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
                                <Box sx={{ position: 'relative' }}>
                                    <Box sx={{
                                        backgroundColor: g.primary,
                                        color: getContrastText(g.primary),
                                        px: 2.5, py: 1,
                                        borderRadius: 1,
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        cursor: 'default',
                                    }}>
                                        Add to cart
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    </>
                    )}

                    {/* Footer */}
                    {vis.footer && (
                        <Box sx={{
                            position: 'relative',
                            backgroundColor: sec.footer.bgColor,
                            color: sec.footer.textColor,
                            py: 2, px: 3,
                            display: 'grid',
                            gridTemplateColumns: `repeat(${sec.footer.columns || 3}, 1fr)`,
                            gap: 2,
                            fontSize: '0.85rem',
                            textAlign: 'center',
                        }}>
                            {Array.from({ length: sec.footer.columns || 3 }).map((_, i) => (
                                <Box key={i}>
                                    {i === 0 && (<>Contact<br/>Address<br/>Name</>)}
                                    {i === 1 && (<>Privacy<br/>Facebook<br/>Instagram</>)}
                                    {i === 2 && (<>Extra<br/>Link</>)}
                                    {i >= 3 && (<>Column {i + 1}<br/>Link<br/>Link</>)}
                                </Box>
                            ))}
                            {sec.footer.showPaymentMethods && (
                                <Box sx={{
                                    gridColumn: '1 / -1',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 1,
                                    mt: 1.5,
                                    pt: 1.5,
                                    borderTop: `1px solid rgba(0,0,0,0.08)`,
                                }}>
                                    {['VISA', 'MC', 'PayPal', 'Bancontact', 'iDEAL'].map((pm) => (
                                        <Box key={pm} sx={{
                                            px: 1, py: 0.25,
                                            backgroundColor: 'rgba(0,0,0,0.06)',
                                            borderRadius: 0.5,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                        }}>{pm}</Box>
                                    ))}
                                </Box>
                            )}
                            <MockIcons openStyleModal={openStyleModal} section="footer" color config top={6} right={8} />
                        </Box>
                    )}

                    {/* Extra footer */}
                    {vis.extra && (
                        <Box sx={{
                            position: 'relative',
                            backgroundColor: sec.footerExtra.bgColor,
                            color: sec.footerExtra.textColor,
                            py: 1.5, px: 3,
                            display: 'grid',
                            gridTemplateColumns: `repeat(${sec.footerExtra.columns || 3}, 1fr)`,
                            gap: 2,
                            fontSize: '0.8rem',
                            textAlign: 'center',
                            borderTop: `1px solid ${borderColor}`,
                        }}>
                            {Array.from({ length: sec.footerExtra.columns || 3 }).map((_, i) => (
                                <span key={i}>Extra {i + 1}</span>
                            ))}
                            <MockIcons openStyleModal={openStyleModal} section="footerExtra" color config top={6} right={8} />
                        </Box>
                    )}
                </Paper>

                {/* Color settings — tiny popover anchored at the clicked brush icon */}
                <Popover
                    open={activeStyleSection?.mode === 'color' && !!activeStyleSection?.anchorPos}
                    anchorReference="anchorPosition"
                    anchorPosition={activeStyleSection?.anchorPos || { top: 0, left: 0 }}
                    onClose={() => {
                        // Safety net: if the user literally just opened the native
                        // color picker, swallow this close — MUI's click-away can
                        // fire right as the OS dialog appears.
                        if (Date.now() - colorPickerInteractionRef.current < 500) return;
                        setActiveStyleSection(null);
                    }}
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 2,
                                mt: 0.5,
                                minWidth: 240,
                                border: `1px solid ${borderColor}`,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            },
                        },
                    }}
                >
                    <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${borderColor}` }}>
                        <BrushIcon sx={{ color: Colors.sft_blue, fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activeStyleSection ? (props.t ? props.t('cfg_style_section_' + activeStyleSection.section, SECTION_LABELS[activeStyleSection.section]) : SECTION_LABELS[activeStyleSection.section]) : ''}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                        {activeStyleSection?.mode === 'color' && renderSectionModalFields()}
                    </Box>
                </Popover>

                {/* Config settings — Dialog widens to `md` for sections that
                    host a multi-card modal (Navigation), and stays compact
                    for the simpler ones (image width, language position, …). */}
                <Dialog
                    open={activeStyleSection?.mode === 'config'}
                    onClose={() => setActiveStyleSection(null)}
                    {...webshopModalSizing(styleModalTier, { collapsed: sidebarCollapsed })}
                >
                    {/* Title is slightly larger than the rest of the chrome
                        so admins can scan which section they're configuring;
                        actions/content stay compact. */}
                    <DialogTitle sx={{ py: 1.25, px: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SettingsIcon sx={{ color: mutedText, fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: '1.1rem' }}>
                                {activeStyleSection ? (props.t ? props.t('cfg_style_section_' + activeStyleSection.section, SECTION_LABELS[activeStyleSection.section]) : SECTION_LABELS[activeStyleSection.section]) : ''}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: mutedText, display: 'block', mt: 0.25, fontSize: '0.78rem' }}>
                            {props.t ? props.t('cfg_style_modal_subtitle', 'Layout & content settings for this section only.') : 'Layout & content settings for this section only.'}
                        </Typography>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ py: 2 }}>
                        {activeStyleSection?.mode === 'config' && renderSectionModalFields()}
                    </DialogContent>
                    <Divider />
                    {/* Same action bar as the Pages block-config dialog:
                        Cancel just closes (edits stay in state, the floating
                        save still has them), Save runs the global saveConfig
                        and closes on success. */}
                    <DialogActions sx={{ px: 1.5, py: 0.75 }}>
                        <Button onClick={() => setActiveStyleSection(null)}>
                            {props.t ? props.t('cancel', 'Cancel') : 'Cancel'}
                        </Button>
                        <ConfigSaveButton saving={configSaving} onSave={saveConfig} onClose={() => setActiveStyleSection(null)} t={props.t} />
                    </DialogActions>
                </Dialog>

                {/* Colour settings opened via the SEARCH bar. The brush Popover
                    above needs an anchor position from the clicked icon, which a
                    search jump doesn't have — so when colour mode is opened
                    WITHOUT an anchor we render the same colour fields in a
                    centered Dialog instead. */}
                <Dialog
                    open={activeStyleSection?.mode === 'color' && !activeStyleSection?.anchorPos}
                    onClose={() => setActiveStyleSection(null)}
                    maxWidth="xs"
                    PaperProps={{ sx: { borderRadius: 3, width: 320, maxWidth: '90vw' } }}
                >
                    <DialogTitle sx={{ py: 1.25, px: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BrushIcon sx={{ color: mutedText, fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: '1.1rem' }}>
                                {activeStyleSection ? (props.t ? props.t('cfg_style_section_' + activeStyleSection.section, SECTION_LABELS[activeStyleSection.section]) : SECTION_LABELS[activeStyleSection.section]) : ''}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: mutedText, display: 'block', mt: 0.25, fontSize: '0.78rem' }}>
                            {props.t ? props.t('cfg_style_colors_subtitle', 'Colours for this section.') : 'Colours for this section.'}
                        </Typography>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ py: 2 }}>
                        {activeStyleSection?.mode === 'color' && !activeStyleSection?.anchorPos && renderSectionModalFields()}
                    </DialogContent>
                    <Divider />
                    <DialogActions sx={{ px: 1.5, py: 0.75 }}>
                        <Button onClick={() => setActiveStyleSection(null)}>
                            {props.t ? props.t('cancel', 'Cancel') : 'Cancel'}
                        </Button>
                        <ConfigSaveButton saving={configSaving} onSave={saveConfig} onClose={() => setActiveStyleSection(null)} t={props.t} />
                    </DialogActions>
                </Dialog>
            </Box>
        );
    };
    return { renderSectionModalFields, renderStyleTab };
};
